import { useContext, useEffect, useState } from "react";
import { fetchBusinessAndEnterpriseDashboardStats } from "../../../api/businessAndEnterprise/businessAndEnterpriseApi";
import FinancialDataEntriesBarChart from "./FinancialDataEntriesBarChart";
import { FiFileText, FiUsers, FiChevronRight } from "react-icons/fi";
import { FaFileAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Context from "../../../context/Context";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const { setPageTitle } = useContext(Context);
  const [isLoading, setIsLoading] = useState(true);

  const handleRedirection = (path) => {
    navigate(path);
  }

  useEffect(() => {
    setPageTitle("Dashboard");
    setIsLoading(true);
    fetchBusinessAndEnterpriseDashboardStats({}, (response) => {
      if (response?.status === true) {
        setStats(response?.data);
      }
       setIsLoading(false); 
    });
  }, [setPageTitle]);

  if (isLoading) {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>
  );
}


  return (
    <div className="p-4 sm:p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border">
              <h2 className="text-xl sm:text-xl font-bold">Number of Sheets</h2>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-4xl sm:text-5xl font-medium my-2 sm:my-4">
                  {stats?.number_of_sheets ?? 0}
                </p>
                <button onClick={() => handleRedirection('/personal/drive')} className="border border-gray-400 px-4 py-1 font-bold rounded text-sm hover:bg-gray-100 w-full sm:w-auto">
                  Start creating
                </button>
              </div>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border">
              <h2 className="text-xl sm:text-xl font-bold">Number of Members</h2>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-4xl sm:text-5xl font-medium my-2 sm:my-4">
                  {stats?.total_members ?? 0}
                </p>
                <button onClick={() => handleRedirection('/company/employees')} className="border border-gray-400 font-bold px-4 py-1 rounded text-sm hover:bg-gray-100 w-full sm:w-auto">
                  Add new
                </button>
              </div>
            </div>
          </div>

          <div>
            {stats &&
              (stats.total_categories >= 0 ||
                stats.total_units >= 0 ||
                stats.total_timeframes >= 0 ||
                stats.total_sheet_companies >= 0) ? (
              <FinancialDataEntriesBarChart stats={stats} />
            ) : (
              <div className="text-center">
                <p className="text-[#000000] font-bold">No entries yet</p>
                <p className="text-[#7E8084] text-xs sm:text-sm mb-4">
                  Try adding financial data entries for categories, companies,
                  units and time period for the data to appear here
                </p>
                <button className="border border-gray-400 font-bold px-4 py-1 rounded text-sm hover:bg-gray-100">
                  Add
                </button>
              </div>
            )}
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl sm:text-xl font-bold">New Members</h2>
              <button onClick={() => handleRedirection('/company/employees')} className="font-bold text-xs sm:text-sm text-black flex items-center">
                View all <FiChevronRight className="ml-1" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {stats?.recent_members.map((member) => {
                const nameParts = member.name.trim().split(" ");
                const initials =
                  nameParts.length > 1
                    ? nameParts[0][0].toUpperCase() + nameParts[nameParts.length - 1][0].toUpperCase()
                    : nameParts[0][0].toUpperCase();

                return (
                  <div key={member.id} className="flex flex-col items-center text-center">
                    {member.profile_image ? (
                      <img
                        src={member.profile_image}
                        alt={member.name}
                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#7A8FA6] flex items-center justify-center text-white font-semibold text-base sm:text-xl">
                        {initials}
                      </div>
                    )}
                    <p className="text-sm sm:text-base mt-2 truncate max-w-[80px]">{member.name}</p>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        <div className="space-y-6">
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl sm:text-xl font-bold">Teams</h2>
              <button onClick={() => handleRedirection('/teams/drive')} className="font-bold text-xs sm:text-sm text-black flex items-center">
                View all <FiChevronRight className="ml-1" />
              </button>
            </div>

            {stats?.teams?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {stats?.teams?.slice(0, 4)?.map((team, idx) => (
                  <div
                    key={idx}
                    className="border rounded-lg p-3 sm:p-4 hover:shadow-md transition bg-white flex flex-col justify-between"
                  >
                    <p className="font-semibold text-sm sm:text-base mb-3 break-words">
                      {team?.name}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center gap-1 min-w-fit">
                        <FiUsers className="h-4 w-4 text-gray-500 shrink-0" />
                        <span className="truncate">{team?.total_members} Members</span>
                      </div>

                      <div className="flex items-center gap-1 min-w-fit">
                        <FiFileText className="h-4 w-4 text-gray-500 shrink-0" />
                        <span className="truncate">{team?.total_sheets} Sheets</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center">
                <p className="text-black font-bold">No teams yet</p>
                <p className="text-gray-500 text-xs sm:text-sm">
                  Your teams will appear here as soon as you create them.
                </p>
              </div>
            )}
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl sm:text-xl font-bold">Recently Created Files</h2>
              <button onClick={() => handleRedirection('/personal/drive')} className="font-bold text-xs sm:text-sm text-black flex items-center">
                View all <FiChevronRight className="ml-1" />
              </button>
            </div>
            {stats?.recent_sheets?.length > 0 ? (
              <ul className="grid grid-cols-1 !pl-0 sm:grid-cols-2 md:grid-cols-3 gap-5 text-sm text-gray-700">
                {stats?.recent_sheets?.slice(0, 3)?.map((sheet, idx) => (
                  <div
                    key={idx}
                    className="border rounded-lg shadow-sm p-2 flex flex-col items-start hover:shadow-md transition cursor-pointer"
                  >
                    <div className="flex items-center text-xs text-gray-500 w-full justify-between">
                      <h4 className="font-semibold !mb-0 text-gray-800 text-sm truncate">
                        {sheet.title}
                      </h4>
                    </div>
                    <div className="flex justify-center w-full">
                      <FaFileAlt className="text-[#8BA4BD] !h-[130px] !w-[130px]" />
                    </div>
                  </div>
                ))}
              </ul>
            ) : (
              <div className="text-center">
                <p className="text-[#000000] font-bold">No files yet</p>
                <p className="text-[#7E8084] text-xs sm:text-sm mb-4">
                  Once you start creating, files will show up here
                </p>
                <button onClick={() => handleRedirection('/personal/drive')} className="border border-gray-400 font-bold px-4 py-1 rounded text-sm hover:bg-gray-100">
                  Start creating
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
